import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import type {
  AutoPrintCatalog,
  AutoPrintRule,
  CreateAutoPrintRuleRequest,
  PrinterAdminRow,
  UpdateAutoPrintRuleRequest,
} from '@techgitter/contracts';
import {
  createAutoPrintRule,
  deleteAutoPrintRule,
  fetchAutoPrintCatalog,
  fetchAutoPrintRules,
  updateAutoPrintRule,
} from '../services/autoPrintRulesApi';
import { fetchPrinters } from '../services/printersApi';
import { AutoPrintRuleEditDialog } from '../components/admin/AutoPrintRuleEditDialog';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';

export const AutoPrintRulesAdminPage: React.FC = () => {
  const [items, setItems] = useState<AutoPrintRule[]>([]);
  const [catalog, setCatalog] = useState<AutoPrintCatalog | null>(null);
  const [printers, setPrinters] = useState<PrinterAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<AutoPrintRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<AutoPrintRule | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Id of the rule whose inline Active toggle is mid-PATCH (disables that switch).
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      const [rules, cat, fleet] = await Promise.all([
        fetchAutoPrintRules(signal, { includeInactive: true }),
        fetchAutoPrintCatalog(signal),
        fetchPrinters(signal, { includeInactive: true }),
      ]);
      setItems(rules);
      setCatalog(cat);
      setPrinters(fleet);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setLoadError(
        err instanceof Error ? err.message : 'Failed to load AutoPrint rules',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const dialogOpen = creating || editing !== null;

  const openCreate = () => {
    setSaveError(null);
    setCreating(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setCreating(false);
    setEditing(null);
    setSaveError(null);
  };

  const handleSave = async (
    payload: CreateAutoPrintRuleRequest | UpdateAutoPrintRuleRequest,
    id: string | null,
  ) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (id === null) {
        const created = await createAutoPrintRule(payload as CreateAutoPrintRuleRequest);
        setItems((current) => [...current, created]);
        setSnackbar({ open: true, message: 'Rule created', severity: 'success' });
      } else {
        const updated = await updateAutoPrintRule(id, payload as UpdateAutoPrintRuleRequest);
        setItems((current) =>
          current.map((it) => (it.id === updated.id ? updated : it)),
        );
        setSnackbar({ open: true, message: 'Rule updated', severity: 'success' });
      }
      setCreating(false);
      setEditing(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (rule: AutoPrintRule) => {
    setTogglingId(rule.id);
    try {
      const updated = await updateAutoPrintRule(rule.id, { isActive: !rule.isActive });
      setItems((current) =>
        current.map((it) => (it.id === updated.id ? updated : it)),
      );
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update rule',
        severity: 'error',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteSubmitting(true);
    try {
      await deleteAutoPrintRule(deleting.id);
      setItems((current) => current.filter((it) => it.id !== deleting.id));
      setSnackbar({ open: true, message: 'Rule deleted', severity: 'success' });
      setDeleting(null);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete rule',
        severity: 'error',
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Document key -> kind, for the kind chip in the Document column.
  const documentKinds = useMemo(() => {
    const map = new Map<string, string>();
    catalog?.documents.forEach((d) => map.set(d.key, d.kind));
    return map;
  }, [catalog]);

  // Sort by trigger, then printer (site, role), then document — so a site's
  // lanes sit together and a trigger's bindings group.
  const sortedItems = useMemo(() => {
    return [...items].sort(
      (a, b) =>
        a.trigger.localeCompare(b.trigger) ||
        a.siteId.localeCompare(b.siteId) ||
        a.role.localeCompare(b.role) ||
        a.document.localeCompare(b.document),
    );
  }, [items]);

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
        <ToolbarButton variant="primary" icon={IconPlus} onClick={openCreate}>
          New rule
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTableCard>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Trigger</DataTableHeadCell>
                  <DataTableHeadCell>Document</DataTableHeadCell>
                  <DataTableHeadCell>Printer</DataTableHeadCell>
                  <DataTableHeadCell>Copies</DataTableHeadCell>
                  <DataTableHeadCell>Active</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <DataTableEmpty colSpan={6}>
                    <Stack alignItems="center" spacing={1.5} sx={{ py: 2 }}>
                      <span>No AutoPrint rules yet.</span>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<IconPlus size={16} stroke={1.5} />}
                        onClick={openCreate}
                      >
                        New rule
                      </Button>
                    </Stack>
                  </DataTableEmpty>
                ) : (
                  sortedItems.map((rule) => {
                    const kind = documentKinds.get(rule.document);
                    const dim = !rule.isActive;
                    return (
                      <DataTableRow key={rule.id}>
                        <DataTableCell bold={!dim} muted={dim}>
                          {rule.trigger}
                        </DataTableCell>
                        <DataTableCell muted={dim}>
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                          >
                            {rule.document}
                            {kind && <Chip tone="info">{kind}</Chip>}
                          </Box>
                        </DataTableCell>
                        <DataTableCell muted={dim}>
                          {rule.siteId} · {rule.role}
                        </DataTableCell>
                        <DataTableCell muted={dim}>
                          {rule.copiesMode === 'fixed'
                            ? (rule.copies ?? '—')
                            : 'per unit'}
                        </DataTableCell>
                        <DataTableCell>
                          <Switch
                            size="small"
                            checked={rule.isActive}
                            disabled={togglingId === rule.id}
                            onChange={() => handleToggleActive(rule)}
                          />
                        </DataTableCell>
                        <DataTableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSaveError(null);
                                setEditing(rule);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeleting(rule)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </DataTableCell>
                      </DataTableRow>
                    );
                  })
                )}
              </tbody>
            </DataTable>
          </DataTableCard>
        )}
      </Box>

      <AutoPrintRuleEditDialog
        open={dialogOpen}
        rule={editing}
        catalog={catalog}
        printers={printers}
        isSaving={saving}
        error={saveError}
        onClose={closeDialog}
        onSave={handleSave}
      />

      <Dialog
        open={deleting !== null}
        onClose={() => !deleteSubmitting && setDeleting(null)}
      >
        <DialogTitle>Delete AutoPrint rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the rule{' '}
            <strong>
              {deleting?.trigger} → {deleting?.document}
            </strong>{' '}
            for <strong>{deleting?.siteId} · {deleting?.role}</strong>? To keep
            it but stop it firing, edit it and turn off “Active” instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
